import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { isPast } from 'date-fns';

import { Page, PageContent } from '@zillow/constellation';
import { Heading } from '@zillow/constellation-10';
import { parseQueryString } from '@zg-rentals/string-utils';

import { RoutingSelectors } from '../../selectors';

import { useFreemiumDetails } from '../../hooks/queries/freemium';

import PostingActions from '../../actions/PostingActions';
import PropertiesActions from '../../actions/PropertiesActions';

import PropertyConstants from '../../constants/PropertyConstants';

import ErrorModal from '../../components/Modal/ErrorModal';
import PaymentError from '../../components/Payment/PaymentError';
import PaymentDetails from '../../components/Payment/PaymentDetails';
import PaymentOptionsModal from '../../components/Modal/PaymentOptionsModal';
import SpinnerContainer from '../../components/SpinnerContainer';
import { logError } from '@zg-rentals/log-error';

const PAYMENT_TYPE = {
  FREEMIUM: 'freemium',
};

const propTypes = {};
const defaultProps = {};

/**
 * Handles the native mobile checkout flow for freemium
 *
 * The URL for the web view must provide the query param: paymentType
 * ex: /payToActivate?hostApp=ios&paymentType=freemium
 *
 * on payment success we will trigger a callback via triggerPaymentSuccessCallback
 */
const MobilePaymentPage = () => {
  const dispatch = useDispatch();

  const location = useLocation();

  const listingAlias = useSelector((state) => RoutingSelectors.makeListingId(state));
  const {
    properties: {
      currentProperty: {
        freemium,
        freemium: {
          activeProduct: { expirationTimestamp },
        },
      },
    },
    payment: { chargeAndActivateListing: { isPaymentValid, error: chargeAndActivateListingError } = {} },
  } = useSelector((state) => state);

  const [displayOptionsModal, setDisplayOptionsModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [headingText, setHeadingText] = useState('Credit card information');

  const { refetch: refetchFreemiumDetails, error: freemiumError } = useFreemiumDetails(
    {
      listingId: listingAlias,
    },
    {
      onSuccess: () => {
        setIsLoading(false);
        dispatch(
          PropertiesActions.updateCurrentProperty({
            selectedProductType: PropertyConstants.PRODUCT_TYPE_NAME_TO_ID.PREMIUM,
          }),
        );
      },
      onError: () => {
        // UI is updated via freemiumDetailsErrorMessage
        setIsLoading(false);
      },
    },
  );

  const isActiveProductExpired = isPast(new Date(expirationTimestamp));

  const { error: { message: freemiumDetailsErrorMessage } = {} } = freemiumError || {};

  const { search = '' } = location || {};
  const { paymentType, activate } = useMemo(() => parseQueryString(search) || {}, [search]);

  /**
   * isPaymentValid - handles paid inclusion property for post charge and activate
   * isActiveProductExpired - handles freemium property for the case when freemium product is already active and not expired
   */
  const shouldHandleValidPayment = isPaymentValid || !isActiveProductExpired;

  useEffect(() => {
    if (paymentType === PAYMENT_TYPE.FREEMIUM) {
      refetchFreemiumDetails();

      return;
    } else {
      // users should not get into this else block but putting a log here to see if mobile is sending an incorrect paymentType. Otherwise we will maye freemium default.
      setIsLoading(false);
      setErrorMessage(
        'We are experiencing an issue with payments. Please contact customer support: rentalsupport@zillow.com',
      );
    }
  }, []);

  useEffect(() => {
    if (freemiumDetailsErrorMessage !== undefined && freemiumDetailsErrorMessage !== '') {
      setErrorMessage(freemiumDetailsErrorMessage);
    }
  }, [freemiumDetailsErrorMessage]);

  useEffect(() => {
    if (shouldHandleValidPayment) {
      triggerPaymentSuccessCallback();
    }
  }, [isPaymentValid, isActiveProductExpired]);

  const handleChargeAndActivateListingError = (chargeAndActivateListingError) => {
    const { data, message } = chargeAndActivateListingError;
    const { listingActivationFailed, activationError, isInMaintenanceMode } = data;
    if (listingActivationFailed) {
      return <ErrorModal error={activationError} />;
    }
    return <PaymentError isInMaintenanceMode={isInMaintenanceMode} errorMessage={message} />;
  };

  const triggerPaymentSuccessCallback = () => {
    try {
      window.ZRM_NATIVE_API.onPaidInclusionPaymentMade('success');
    } catch (error) {
      logError({
        error,
        errorType: 'ZrmNativeApiError',
        context: 'This function is intended to be triggered inside a WebView. Check context.userAgent',
      });
    }
  };

  const handleUpdateDisplayPaymentOptionsModal = (value) => {
    setDisplayOptionsModal(value);
  };

  const handleFreemiumChargeSuccess = (response) => {
    if (activate === 'false') {
      if (response.isPaymentValid) {
        triggerPaymentSuccessCallback();
      }

      return;
    }

    if (response.isPaymentValid) {
      activateListing(response);
    }
  };

  const activateListing = (freemiumChargeResponse) => {
    return dispatch(PostingActions.property.activate(listingAlias, false))
      .then(({ response: activateResponse }) => {
        // sync what is available without having to do extra product lookups
        // expirationTimestamp is critical to update
        dispatch(
          PropertiesActions.updateCurrentProperty({
            daysExtended: activateResponse?.daysExtended,
            freemium: {
              ...freemium,
              activeProduct: {
                ...freemium.activeProduct,
                type: freemiumChargeResponse?.activeProductType,
                expirationTimestamp: activateResponse?.expirationDateTimestamp,
              },
            },
          }),
        );
      })
      .catch((errorRMWModel) => {
        handleActivationError(errorRMWModel);
      });
  };

  const handleActivationError = (error) => {
    const { data, message: errorMessage } = error;
    const { listingActivationFailed, activationError } = data || {};

    // general message
    let message = errorMessage;

    if (listingActivationFailed) {
      if (activationError?.message) {
        // more specific message
        message = activationError?.message;
      }
    }

    setErrorMessage(message);
  };

  const onFreemiumChargeError = (error) => {
    setErrorMessage(error?.error?.message);
  };

  const renderComponent = () => {
    if (errorMessage !== '') {
      return <Heading level={5}>{errorMessage}</Heading>;
    }

    if (shouldHandleValidPayment) {
      return <Heading level={5}>Payment is complete</Heading>;
    }

    if (chargeAndActivateListingError) {
      return <div>{handleChargeAndActivateListingError(chargeAndActivateListingError)}</div>;
    }

    return (
      <div>
        <Heading level={6} data-testid="MobilePaymentPage-Title">
          {headingText}
        </Heading>

        {displayOptionsModal ? (
          <PaymentOptionsModal
            isOpen={displayOptionsModal}
            onOptionSelected={() => setDisplayOptionsModal(false)}
            updateDisplayPaymentOptionsModal={handleUpdateDisplayPaymentOptionsModal}
          />
        ) : (
          <PaymentDetails
            listingAlias={listingAlias}
            isRenderedInPageView={true}
            handleFreemiumChargeSuccess={handleFreemiumChargeSuccess}
            onFreemiumChargeError={onFreemiumChargeError}
            setHeadingText={setHeadingText}
          />
        )}
      </div>
    );
  };

  return (
    <Page data-testid="MobilePaymentPage">
      <PageContent>{isLoading ? <SpinnerContainer /> : renderComponent()}</PageContent>
    </Page>
  );
};

MobilePaymentPage.propTypes = propTypes;
MobilePaymentPage.defaultProps = defaultProps;

export default MobilePaymentPage;
